import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import PainPoints from './components/PainPoints';
import WhyButlerWorks from './components/WhyButlerWorks';
import RecommendedPrograms from './components/RecommendedPrograms';
import ScenarioTraining from './components/ScenarioTraining';
import TargetRoles from './components/TargetRoles';
import PricingTable from './components/PricingTable';
import FlowSteps from './components/FlowSteps';
import ContactForm from './components/ContactForm';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function CorporateResidencePage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();

  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t('corporateResidence.jsonld.serviceName'),
    provider: {
      '@type': 'Organization',
      name: t('corporateResidence.jsonld.orgName'),
    },
    serviceType: t('corporateResidence.jsonld.serviceType'),
    areaServed: {
      '@type': 'Country',
      name: lang === 'en' ? 'Japan' : t('corporateResidence.jsonld.areaServed'),
    },
    description: t('corporateResidence.jsonld.description'),
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('corporateResidence.jsonld.breadcrumb1'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('corporateResidence.jsonld.breadcrumb2'), item: 'https://butler-academy.jp/corporate' },
      { '@type': 'ListItem', position: 3, name: t('corporateResidence.jsonld.breadcrumb3'), item: 'https://butler-academy.jp/corporate/residence' },
    ],
  };

  return (
    <>
      <Header />
      <main>
      <Seo route="/corporate/residence" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />

      <HeroSection />

      <FadeInSection>
        <PainPoints />
      </FadeInSection>

      <FadeInSection>
        <WhyButlerWorks />
      </FadeInSection>

      <FadeInSection>
        <RecommendedPrograms />
      </FadeInSection>

      <FadeInSection>
        <ScenarioTraining />
      </FadeInSection>

      <FadeInSection>
        <TargetRoles />
      </FadeInSection>

      <FadeInSection>
        <PricingTable />
      </FadeInSection>

      <FadeInSection>
        <FlowSteps />
      </FadeInSection>

      <FadeInSection>
        <ContactForm />
      </FadeInSection>

      <FadeInSection>
        <FAQSection />
      </FadeInSection>

      <FadeInSection>
        <CTASection />
      </FadeInSection>

      <FadeInSection>
        <RelatedPages />
      </FadeInSection>

      <div className="bg-background-50 py-8">
        {String(t('corporateResidence.page.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{__s}</p>))}
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
        aria-label={t('corporateResidence.page.backToTop')}
      >
        <i className="ri-arrow-up-line"></i>
      </button>
    </main>
      <Footer />
    </>
  );
}